// RecurringOccurrenceChooser.jsx — This occurrence vs Entire series

import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { addExDateToRecurrence } from 'utilities/DateTimeRangeRecurrence';
import { updateNode } from 'services/nodes/NodeWriteService';
import {
  getCalendarFieldSectionData,
  getCalendarFieldStorageKey,
} from 'views/calendar/shared/calendarFieldUpdate';

import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import ModalMessage from 'components/modal/message/ModalMessage';

import './RecurringOccurrenceChooser.css';

function hasRecurrence(item) {
  return Boolean(
    item?.isRecurringOccurrence
    || item?.rawValue?.recurrence
  );
}

/**
 * RecurringOccurrenceChooser
 *
 * @param {object|null} item - Calendar event item with occurrence metadata.
 * @param {Function} onClose - Close chooser.
 * @param {Function} onOpenSeries - Open master node (entire series).
 * @param {object} typeDef - Type definition for storage key resolution.
 */
const RecurringOccurrenceChooser = ({
  item,
  onClose,
  onOpenSeries,
  typeDef = null,
}) => {
  const { theme } = useTheme();
  const { user } = useUser();
  const [busy, setBusy] = useState(false);

  if (!item || !hasRecurrence(item)) return null;

  const title = item?.node?.title || 'Event';
  const occurrenceLabel = item?.occurrenceDateKey
    ? `Occurrence ${item.occurrenceDateKey}`
    : 'This occurrence';

  const handleOpenSeries = () => {
    onOpenSeries?.(item.node);
    onClose?.();
  };

  const handleCancelOccurrence = async () => {
    const node = item?.node;
    const fieldName = item?.field?.name;
    const occurrenceDateKey = item?.occurrenceDateKey;
    if (!node?.key || !fieldName || !occurrenceDateKey) {
      toast.error('Could not cancel this occurrence.');
      return;
    }

    const storageKey = getCalendarFieldStorageKey(
      node,
      item.field,
      typeDef?.key || node?.type,
    );
    if (!storageKey) {
      toast.error('Could not cancel this occurrence: storage unavailable.');
      return;
    }

    const currentValue = item.rawValue && typeof item.rawValue === 'object'
      ? { ...item.rawValue }
      : null;
    if (!currentValue?.recurrence) {
      toast.error('This event is not part of a series.');
      return;
    }

    const nextRecurrence = addExDateToRecurrence(currentValue.recurrence, occurrenceDateKey);
    if (!nextRecurrence) {
      toast.error('Could not update the series.');
      return;
    }

    setBusy(true);
    try {
      const section = getCalendarFieldSectionData(node, storageKey);
      await updateNode(node.key, {
        [storageKey]: {
          ...section,
          [fieldName]: {
            ...currentValue,
            recurrence: nextRecurrence,
          },
        },
      }, {
        actor: user || null,
      });
      toast.success('Cancelled this occurrence.');
      onClose?.();
    } catch (error) {
      toast.error(error?.message || 'Failed to cancel occurrence.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Recurring event"
      isOpen
      width={420}
      height={280}
      onClose={onClose}
    >
      <ModalForm>
        <div className="recurring-occurrence-chooser">
          <ModalMessage
            text={`${title} — ${occurrenceLabel}. Choose whether to open the whole series or cancel just this day.`}
          />
          <div className="recurring-occurrence-chooser__hint" style={{ color: theme.foregroundMuted }}>
            Changing the time for only this day is not supported yet — open the series to change all times.
          </div>
        </div>
      </ModalForm>
      <ModalButtons>
        <Button
          label="Cancel this occurrence"
          enabled={!busy}
          onClick={handleCancelOccurrence}
        />
        <Button
          label="Entire series"
          enabled={!busy}
          onClick={handleOpenSeries}
        />
      </ModalButtons>
    </Modal>
  );
};

RecurringOccurrenceChooser.displayName = 'RecurringOccurrenceChooser';

export default RecurringOccurrenceChooser;
export { hasRecurrence };
